import { BudgetPhases } from 'business/constants';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CHAPTER_FIELD,
    CHAPTER_SOFTWARE_ID,
    REPORTED_FIELD,
} from 'routes/BudgetModule/config/constants';
import {
    getFieldByExercice,
    translateMultipleName,
} from 'routes/BudgetModule/helpers/format';
import {
    getCadrageValueForResource,
    getSumOfMetric,
    sumMultipleMetricByDim,
} from 'routes/BudgetModule/helpers/dataOperation';
import { BudgetProposalT, CadrageT } from 'routes/BudgetModule/config/types';
import AutosizerFlex from 'components/AutosizerFlex';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { StyHeader, StyTitle, StyVerticalLayout } from './styles';
import { ExpenseIncomeImputationDataT } from '../../hooks/expenseIncomeImputations';
import CadrageGaugeChart from './CadrageGaugeChart';
import { Button } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import CadrageChapterDetailsModal from './CadrageChapterDetailsModal';
import { trackEventClickCadrageByChapterGraph } from 'routes/Admin/Budget/CadrageManagement/helper';
import { translateBlockValue } from 'i18n';
import {
    getMetricByYear,
    getPhaseSettings,
} from 'routes/BudgetModule/shared/phases/phase';
import { useGetPhases } from 'routes/BudgetModule/shared/phases/hook';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';

type Props = {
    budgetProposal: BudgetProposalT;
    cadrageData: CadrageT[];
    filteredImputationData: ExpenseIncomeImputationDataT[];
    dataByPhase: (InscriptionT | ResponseDataT)[];
    isReportedAmountUsed: boolean;
    reverseColor: boolean;
    hasOrphanInscriptions: boolean;
    resourceSoftwareId: string;
};

const CadrageChart = ({
    budgetProposal,
    cadrageData,
    filteredImputationData,
    dataByPhase,
    isReportedAmountUsed,
    reverseColor,
    hasOrphanInscriptions,
    resourceSoftwareId,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [
        isCadrageChaperDetailsModalOpen,
        setIsCadrageChaperDetailsModalOpen,
    ] = useState(false);

    const { phase, exercice } = budgetProposal;
    const { data: phases = [] } = useGetPhases();
    const statusesSettings = useContext(StatusSettingsContext);
    const phaseSettings = getPhaseSettings(phases, phase);

    /**
    Settings for the chart showing the simulated amount by the user against the proposed amount by the services.
    The cadrage value is reference value to decide whether proposed amount is wise.
     */
    const amountField = getMetricByYear(phaseSettings, exercice);

    let sumAmountToCompareByPhase = getSumOfMetric(dataByPhase, amountField);
    if (isReportedAmountUsed) {
        sumAmountToCompareByPhase += getSumOfMetric(
            filteredImputationData,
            getFieldByExercice(REPORTED_FIELD, exercice),
        );
    }

    // In the supplementary phase, the sum of the inscription represents the total budget not the
    // supplementary budget
    const amountFieldLabel =
        phase === BudgetPhases.SUPPLEMENTARY_BUDGET
            ? t('budgetProposal.totalBudget')
            : translateMultipleName(amountField);

    const chartData = {
        metric: amountFieldLabel,
        value: sumAmountToCompareByPhase,
    };
    const cadrageValue = getCadrageValueForResource(
        resourceSoftwareId,
        cadrageData,
    );

    const amountByChapters = sumMultipleMetricByDim(
        dataByPhase,
        [amountField],
        CHAPTER_FIELD,
        statusesSettings,
    )
        .filter((item) => item[CHAPTER_FIELD] !== '')
        .map((item) => ({
            chapter: item[CHAPTER_FIELD] as string,
            amount: item[amountField] as number,
        }));

    const handleCadrageButtonClick = () => {
        setIsCadrageChaperDetailsModalOpen(true);
        trackEventClickCadrageByChapterGraph();
    };

    return (
        <StyVerticalLayout>
            <StyHeader>
                <StyTitle variant="body1">
                    {t('budgetProposal.cadrageChartTitle')}
                </StyTitle>
            </StyHeader>
            <AutosizerFlex>
                {({ height, width }) => (
                    <CadrageGaugeChart
                        cadrageValue={cadrageValue}
                        data={chartData}
                        height={height}
                        width={width}
                        reverseColor={reverseColor}
                    />
                )}
            </AutosizerFlex>
            {budgetProposal.cadrageByChapter && (
                <Button
                    onClick={handleCadrageButtonClick}
                    variant="outlined"
                    endIcon={<ChevronRight />}
                    sx={{
                        width: 'fit-content',
                        alignSelf: 'center',
                        mb: '10px',
                    }}
                >
                    {t('budgetProposal.cadrageChapterDetails', {
                        chapter: translateBlockValue(
                            CHAPTER_SOFTWARE_ID,
                            true,
                        ).toLowerCase(),
                    })}
                </Button>
            )}
            {isCadrageChaperDetailsModalOpen && (
                <CadrageChapterDetailsModal
                    isModalShown={isCadrageChaperDetailsModalOpen}
                    setShowModal={setIsCadrageChaperDetailsModalOpen}
                    hasOrphanInscriptions={hasOrphanInscriptions}
                    cadrageData={cadrageData.filter(
                        (item) => item.resource === resourceSoftwareId,
                    )}
                    amountByChapters={amountByChapters}
                    amountFieldLabel={amountFieldLabel}
                    reverseColor={reverseColor}
                />
            )}
        </StyVerticalLayout>
    );
};

export default CadrageChart;
